<template>
  <Teleport to="body">
    <transition name="modal-transition">
      <Modal
        v-if="open"
        @cancel="cancel"
        @confirm="confirm"
        :style="style"
        :closeOnBackdropClick="closeOnBackdropClick"
        :data-test-id="$attrs['data-test-id']"
      >
        <slot />
      </Modal>
    </transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { pseudoRandomId } from '@utils/pseudoRandomId';

export default defineComponent({
  name: 'PortalModal',

  inheritAttrs: false,

  emits: { cancel: null, confirm: null },

  props: {
    open: {
      type: Boolean,
      required: true,
    },

    closeOnBackdropClick: {
      type: Boolean,
      default: true,
    },

    type: {
      type: String,
      default: pseudoRandomId(),
    },
  },

  data(): {
    style: Record<string, string>;
  } {
    return {
      style: null,
    };
  },

  computed: {
    ...mapGetters({
      topModal: 'modal/topModal',
    }),
  },

  methods: {
    cancel() {
      this.$emit('cancel');
    },

    confirm() {
      this.$emit('confirm');
    },
  },

  watch: {
    open: {
      // Update the style when the component is created to ensure the zIndex is correct.
      // Some modals need to be open on creation so the "open" value is not updated (and the watcher not fired).
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal) {
          this.style = this.topModal ? { zIndex: this.topModal.zIndex + 1 } : null;

          // Add this modal to the store
          // This is used so other components like Overlay know
          // if a Modal has been opened and can adjust their behavior accordingly
          this.$store.dispatch('modal/show', {
            type: this.type,
            stackable: true,
          });
        } else if (oldVal) {
          // necessary so that modal hiding is fired after possible DOM events like click
          // have finished
          requestAnimationFrame(() => {
            this.$store.dispatch('modal/hide', {
              type: this.type,
            });
          });
        }
      },
    },
  },

  beforeUnmount() {
    if (this.open) {
      this.$store.dispatch('modal/hide', {
        type: this.type,
      });
    }
  },
});
</script>
